import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';
import EquipoService from '../../services/EquipoServiceFirestore';
import { SocioServiceFirestore, Socio } from '../../services/SocioServiceFirestore';
import { FaSave, FaArrowLeft, FaUsers, FaUser } from '../../components/Icons';
import { theme } from '../../theme';
import ImageUploader from '../../components/ImageUploader';

interface Equipo {
  id?: string;
  nombre: string;
  descripcion?: string;
  socios: string[]; // array de ids de socios
  imagenCoche?: string; // URL de la imagen del coche/skin
}

// Componentes estilizados
const Container = styled.div`
  padding: 24px;
  max-width: 1280px;
  margin: 0 auto;
  background-color: ${theme.colors.background.default};
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const FormContainer = styled.div`
  background-color: ${theme.colors.background.paper};
  border-radius: ${theme.borderRadius.medium};
  padding: 32px;
  box-shadow: ${theme.shadows.medium};
  width: 100%;
  max-width: 600px;
  border: 1px solid rgba(0, 0, 0, 0.05);
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 32px;
`;

const Title = styled.h1`
  color: ${theme.colors.primary.main};
  font-weight: ${theme.typography.fontWeights.bold};
  font-size: 2.2rem;
  position: relative;
  padding-bottom: 12px;
  display: flex;
  align-items: center;
  margin: 0;
  
  &:after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    width: 60px;
    height: 4px;
    background-color: ${theme.colors.secondary.main};
    border-radius: 2px;
  }
  
  svg {
    margin-right: 16px;
    color: ${theme.colors.secondary.main};
  }
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Label = styled.label`
  font-weight: ${theme.typography.fontWeights.semibold};
  color: ${theme.colors.text.primary};
  font-size: 1rem;
  display: flex;
  align-items: center;
  
  svg {
    margin-right: 8px;
    color: ${theme.colors.secondary.main};
  }
`;

const Input = styled.input`
  width: 100%;
  padding: 12px 16px;
  border: 1px solid rgba(0, 0, 0, 0.1);
  border-radius: ${theme.borderRadius.small};
  font-size: 1rem;
  transition: all 0.2s ease;
  background-color: ${theme.colors.background.default};
  color: ${theme.colors.text.primary};
  
  &:focus {
    outline: none;
    border-color: ${theme.colors.primary.main};
    box-shadow: 0 0 0 2px rgba(16, 19, 37, 0.1);
  }
  
  &::placeholder {
    color: ${theme.colors.text.hint};
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 12px 16px;
  border: 1px solid rgba(0, 0, 0, 0.1);
  border-radius: ${theme.borderRadius.small};
  font-size: 1rem;
  min-height: 120px;
  transition: all 0.2s ease;
  background-color: ${theme.colors.background.default};
  color: ${theme.colors.text.primary};
  resize: vertical;
  
  &:focus {
    outline: none;
    border-color: ${theme.colors.primary.main};
    box-shadow: 0 0 0 2px rgba(16, 19, 37, 0.1);
  }
  
  &::placeholder {
    color: ${theme.colors.text.hint};
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 12px 16px;
  border: 1px solid rgba(0, 0, 0, 0.1);
  border-radius: ${theme.borderRadius.small};
  font-size: 1rem;
  min-height: 150px;
  transition: all 0.2s ease;
  background-color: ${theme.colors.background.default};
  color: ${theme.colors.text.primary};
  
  &:focus {
    outline: none;
    border-color: ${theme.colors.primary.main};
    box-shadow: 0 0 0 2px rgba(16, 19, 37, 0.1);
  }
  
  option {
    padding: 8px;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 16px;
`;

const Button = styled.button`
  background-color: ${theme.colors.secondary.main};
  color: ${theme.colors.secondary.contrastText};
  border: none;
  border-radius: ${theme.borderRadius.small};
  padding: 12px 20px;
  cursor: pointer;
  display: flex;
  align-items: center;
  font-weight: ${theme.typography.fontWeights.medium};
  transition: all 0.3s ease;
  box-shadow: ${theme.shadows.small};
  font-size: 1rem;
  
  &:hover {
    background-color: ${theme.colors.secondary.dark};
    transform: translateY(-2px);
    box-shadow: ${theme.shadows.medium};
  }
  
  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
    transform: none;
  }
  
  svg {
    margin-right: 10px;
    font-size: 1.1rem;
  }
`;

const CancelButton = styled(Button)`
  background-color: ${theme.colors.background.dark};
  
  &:hover {
    background-color: ${theme.colors.primary.light};
  }
`;

const SectionTitle = styled.h2`
  color: ${theme.colors.primary.main};
  margin-top: 16px;
  margin-bottom: 16px;
  font-size: 1.5rem;
  font-weight: ${theme.typography.fontWeights.semibold};
  position: relative;
  padding-bottom: 8px;
  display: flex;
  align-items: center;
  
  &:after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    width: 40px;
    height: 3px;
    background-color: ${theme.colors.secondary.main};
    border-radius: 2px;
  }
  
  svg {
    margin-right: 12px;
    color: ${theme.colors.secondary.main};
  }
`;

const EquipoDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [formData, setFormData] = useState<Equipo>({
    nombre: '',
    descripcion: '',
    socios: [],
    imagenCoche: ''
  });
  const [socios, setSocios] = useState<Socio[]>([]);
  const [sociosDisponibles, setSociosDisponibles] = useState<Socio[]>([]);
  const [sociosSeleccionados, setSociosSeleccionados] = useState<Socio[]>([]);
  const [socioDisponibleSeleccionado, setSocioDisponibleSeleccionado] = useState<string | null>(null);
  const [socioEquipoSeleccionado, setSocioEquipoSeleccionado] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    SocioServiceFirestore.getAll().then(setSocios);
    if (id && id !== 'new') {
      EquipoService.getById(id).then(equipo => {
        if (equipo) setFormData({ ...equipo, socios: Array.isArray(equipo.socios) ? equipo.socios : [] });
      });
    }
  }, [id]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  // Actualizar los socios disponibles y seleccionados cuando cambian los socios o el equipo
  useEffect(() => {
    if (socios.length > 0) {
      const seleccionados = socios.filter(s => formData.socios.includes(s.id || ''));
      const disponibles = socios.filter(s => !formData.socios.includes(s.id || ''));
      setSociosSeleccionados(seleccionados);
      setSociosDisponibles(disponibles);
    }
  }, [socios, formData.socios]);

  // Mover un socio de disponibles a seleccionados
  const moverASociosSeleccionados = () => {
    if (!socioDisponibleSeleccionado) return;

    const nuevaSeleccion = [...formData.socios, socioDisponibleSeleccionado];
    setFormData(prev => ({ ...prev, socios: nuevaSeleccion }));
    setSocioDisponibleSeleccionado(null);
  };

  // Mover un socio de seleccionados a disponibles
  const moverASociosDisponibles = () => {
    if (!socioEquipoSeleccionado) return;

    const nuevaSeleccion = formData.socios.filter(id => id !== socioEquipoSeleccionado);
    setFormData(prev => ({ ...prev, socios: nuevaSeleccion }));
    setSocioEquipoSeleccionado(null);
  };

  // Mover todos los socios disponibles a seleccionados
  const moverTodosASociosSeleccionados = () => {
    const todosIds = [...formData.socios, ...sociosDisponibles.map(s => s.id || '')];
    setFormData(prev => ({ ...prev, socios: todosIds }));
  };

  // Mover todos los socios seleccionados a disponibles
  const moverTodosASociosDisponibles = () => {
    setFormData(prev => ({ ...prev, socios: [] }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      let equipoId = id;
      let equipoNombre = formData.nombre;
      let sociosPrevios: string[] = [];
      let nombreAnterior = '';
      if (!id || id === 'new') {
        const nuevo = await EquipoService.create(formData);
        equipoId = nuevo.id;
        equipoNombre = nuevo.nombre;
      } else {
        // Obtener socios previos para limpiar EQUIPO y detectar cambio de nombre
        const equipoPrevio = await EquipoService.getById(id);
        sociosPrevios = equipoPrevio?.socios || [];
        nombreAnterior = equipoPrevio?.nombre || '';
        await EquipoService.update(id, formData);
      }
      // Actualizar EQUIPO en socios: poner nombre a los seleccionados, limpiar a los que ya no estén
      const sociosAAsignar = formData.socios;
      const sociosALimpiar = sociosPrevios.filter(sid => !sociosAAsignar.includes(sid));
      // Si el nombre ha cambiado, actualiza el campo EQUIPO de todos los socios asociados
      if (nombreAnterior && equipoNombre !== nombreAnterior) {
        await Promise.all(sociosPrevios.map(async sid => {
          const socio = await SocioServiceFirestore.getById(sid);
          if (socio && socio.EQUIPO === nombreAnterior) {
            await SocioServiceFirestore.update(sid, { EQUIPO: equipoNombre });
          }
        }));
      }
      // Asignar equipo a los nuevos socios
      await Promise.all(sociosAAsignar.map(async sid => {
        await SocioServiceFirestore.update(sid, { EQUIPO: equipoNombre });
      }));
      // Limpiar equipo en los socios que ya no están
      await Promise.all(sociosALimpiar.map(async sid => {
        await SocioServiceFirestore.update(sid, { EQUIPO: '' });
      }));
      navigate('/equipos');
    } catch (err) {
      alert('Error guardando equipo: ' + (err instanceof Error ? err.message : String(err)));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Container>
      {/* Infografía visual del equipo tipo F1 */}
      <div style={{
        width: '100%',
        maxWidth: 600,
        margin: '0 0 32px 0', // solo margen inferior
        background: '#fff',
        borderRadius: '18px',
        boxShadow: '0 6px 32px 0 rgba(0,0,0,0.08)',
        padding: 0,
        overflow: 'hidden',
        border: '2px solid #ececec',
      }}>
        {/* Imagen del coche */}
        {formData.imagenCoche && (
          <div style={{
            width: '100%',
            background: '#f5f6fa',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderBottom: '1px solid #eee',
            padding: '18px 0 10px 0',
          }}>
            <img src={formData.imagenCoche} alt="Coche del equipo"
  style={{
    width: '100%',
    height: '160px',
    objectFit: 'contain',
    borderRadius: '8px',
    boxShadow: '0 2px 8px #0002',
    background: '#f5f6fa',
    display: 'block',
  }}
/>
          </div>
        )}
        {/* Nombre y descripción */}
        <div style={{ padding: '16px 24px 12px 24px', textAlign: 'center' }}>
          <div style={{ fontSize: '2.1rem', fontWeight: 700, color: theme.colors.primary.main, marginBottom: 4, letterSpacing: 1 }}>{formData.nombre || 'Nombre del equipo'}</div>
          <div style={{ fontSize: '1.05rem', color: theme.colors.text.secondary, marginBottom: 16, fontStyle: 'italic' }}>{formData.descripcion || 'Sin descripción'}</div>
          {/* Pilotos del equipo */}
          <div style={{ display: 'flex', justifyContent: 'center', gap: 18, flexWrap: 'wrap', marginBottom: 6 }}>
            {sociosSeleccionados.map(socio => (
              <div key={socio.id} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 60 }}>
                <div style={{
                  width: 48,
                  height: 48,
                  borderRadius: '50%',
                  overflow: 'hidden',
                  border: '2px solid #eee',
                  boxShadow: '0 2px 8px #0001',
                  backgroundColor: '#fafbfc',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginBottom: 4
                }}>
                  {socio.fotoUrl ? (
                    <img src={socio.fotoUrl} alt={socio.display_name || socio.nombre || ''} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  ) : (
                    <FaUser size={22} color="#bbb" />
                  )}
                </div>
                <div style={{ fontSize: '0.93rem', color: theme.colors.text.primary, fontWeight: 500, textAlign: 'center', maxWidth: 80, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {socio.display_name || socio.NOMBRE_CORTO || socio.nombre || 'Sin nombre'}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
      <FormContainer>
        <Header>
          <Title><FaUsers /> {id === 'new' ? 'Nuevo Equipo' : 'Editar Equipo'}</Title>
        </Header>
        
        <Form onSubmit={handleSubmit}>
          <SectionTitle><FaUsers /> Información del Equipo</SectionTitle>
          
          <FormGroup>
            <Label>Nombre</Label>
            <Input
              name="nombre"
              value={formData.nombre}
              onChange={handleChange}
              required
              placeholder="Nombre del equipo"
            />
          </FormGroup>
          
          <FormGroup>
            <Label>Descripción</Label>
            <TextArea 
              name="descripcion" 
              value={formData.descripcion || ''} 
              onChange={handleChange} 
              placeholder="Descripción del equipo"
            />
          </FormGroup>
          
          <FormGroup>
            <Label>Imagen del Coche</Label>
            <ImageUploader
              currentImageUrl={formData.imagenCoche}
              onImageChange={(imageUrl) => setFormData({...formData, imagenCoche: imageUrl})}
              folder="equipos"
              placeholder="https://..."
            />
          </FormGroup>
          
          <FormGroup>
            <Label><FaUsers /> Seleccionar Pilotos del Equipo</Label>
            <div style={{ display: 'flex', gap: '16px', marginTop: '8px' }}>
              {/* Columna izquierda: Pilotos disponibles */}
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <div style={{ fontWeight: 'bold', fontSize: '0.9rem', color: '#666' }}>Pilotos Disponibles</div>
                <div style={{
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  height: '250px',
                  overflow: 'auto',
                  backgroundColor: '#fff'
                }}>
                  {sociosDisponibles.length === 0 ? (
                    <div style={{ padding: '12px', color: '#999', fontStyle: 'italic', textAlign: 'center' }}>
                      No hay pilotos disponibles
                    </div>
                  ) : (
                    <div>
                      {sociosDisponibles.map(socio => (
                        <div
                          key={socio.id}
                          onClick={() => setSocioDisponibleSeleccionado(socio.id || '')}
                          style={{
                            padding: '10px 12px',
                            borderBottom: '1px solid #eee',
                            cursor: 'pointer',
                            backgroundColor: socioDisponibleSeleccionado === socio.id ? '#e6f7ff' : 'transparent',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px'
                          }}
                        >
                          <div style={{
                            width: '24px',
                            height: '24px',
                            borderRadius: '50%',
                            overflow: 'hidden',
                            backgroundColor: '#f0f0f0',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}>
                            {socio.fotoUrl ? (
                              <img 
                                src={socio.fotoUrl} 
                                alt="" 
                                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                              />
                            ) : (
                              <FaUser size={12} color="#999" />
                            )}
                          </div>
                          <div>
                            {socio.display_name || socio.NOMBRE_CORTO || socio.nombre || 'Sin nombre'}
                            {socio.Categoria && (
                              <span style={{ fontSize: '0.8rem', color: '#666', marginLeft: '4px' }}>
                                ({socio.Categoria})
                              </span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              
              {/* Botones centrales */}
              <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: '16px' }}>
                <button
                  type="button"
                  onClick={moverASociosSeleccionados}
                  disabled={!socioDisponibleSeleccionado}
                  style={{
                    padding: '8px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    backgroundColor: socioDisponibleSeleccionado ? theme.colors.secondary.main : '#f0f0f0',
                    color: socioDisponibleSeleccionado ? '#fff' : '#999',
                    cursor: socioDisponibleSeleccionado ? 'pointer' : 'default',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  &gt;
                </button>
                <button
                  type="button"
                  onClick={moverTodosASociosSeleccionados}
                  disabled={sociosDisponibles.length === 0}
                  style={{
                    padding: '8px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    backgroundColor: sociosDisponibles.length > 0 ? theme.colors.secondary.main : '#f0f0f0',
                    color: sociosDisponibles.length > 0 ? '#fff' : '#999',
                    cursor: sociosDisponibles.length > 0 ? 'pointer' : 'default',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  &gt;&gt;
                </button>
                <button
                  type="button"
                  onClick={moverASociosDisponibles}
                  disabled={!socioEquipoSeleccionado}
                  style={{
                    padding: '8px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    backgroundColor: socioEquipoSeleccionado ? theme.colors.primary.main : '#f0f0f0',
                    color: socioEquipoSeleccionado ? '#fff' : '#999',
                    cursor: socioEquipoSeleccionado ? 'pointer' : 'default',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  &lt;
                </button>
                <button
                  type="button"
                  onClick={moverTodosASociosDisponibles}
                  disabled={sociosSeleccionados.length === 0}
                  style={{
                    padding: '8px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    backgroundColor: sociosSeleccionados.length > 0 ? theme.colors.primary.main : '#f0f0f0',
                    color: sociosSeleccionados.length > 0 ? '#fff' : '#999',
                    cursor: sociosSeleccionados.length > 0 ? 'pointer' : 'default',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  &lt;&lt;
                </button>
              </div>
              
              {/* Columna derecha: Pilotos del equipo */}
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <div style={{ fontWeight: 'bold', fontSize: '0.9rem', color: '#666' }}>Pilotos del Equipo</div>
                <div style={{
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  height: '250px',
                  overflow: 'auto',
                  backgroundColor: '#fff'
                }}>
                  {sociosSeleccionados.length === 0 ? (
                    <div style={{ padding: '12px', color: '#999', fontStyle: 'italic', textAlign: 'center' }}>
                      No hay pilotos en el equipo
                    </div>
                  ) : (
                    <div>
                      {sociosSeleccionados.map(socio => (
                        <div
                          key={socio.id}
                          onClick={() => setSocioEquipoSeleccionado(socio.id || '')}
                          style={{
                            padding: '10px 12px',
                            borderBottom: '1px solid #eee',
                            cursor: 'pointer',
                            backgroundColor: socioEquipoSeleccionado === socio.id ? '#e6f7ff' : 'transparent',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px'
                          }}
                        >
                          <div style={{
                            width: '24px',
                            height: '24px',
                            borderRadius: '50%',
                            overflow: 'hidden',
                            backgroundColor: '#f0f0f0',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}>
                            {socio.fotoUrl ? (
                              <img 
                                src={socio.fotoUrl} 
                                alt="" 
                                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                              />
                            ) : (
                              <FaUser size={12} color="#999" />
                            )}
                          </div>
                          <div>
                            {socio.display_name || socio.NOMBRE_CORTO || socio.nombre || 'Sin nombre'}
                            {socio.Categoria && (
                              <span style={{ fontSize: '0.8rem', color: '#666', marginLeft: '4px' }}>
                                ({socio.Categoria})
                              </span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </FormGroup>
          
          <ButtonGroup>
            <Button type="submit" disabled={saving}>
              <FaSave /> {saving ? 'Guardando...' : 'Guardar'}
            </Button>
            <CancelButton type="button" onClick={() => navigate('/equipos')}>
              <FaArrowLeft /> Cancelar
            </CancelButton>
          </ButtonGroup>
        </Form>
      </FormContainer>
    </Container>
  );
};

export default EquipoDetail;
